import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import Logger from '../../utils/logger';
import { minifyBytes } from '../../utils/conversion';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
	addBlock,
	cancelFileUpload,
	getUploadComplete,
	initFileUpload,
	isSuspectKnown,
	setFileFormat,
	skipFileUpload,
	updateStatus,
} from '../../store/upload/uploadSlice';
import { UploadStatus } from '../../types/generic';
import { checkFileExists, generateBlockBlobClient } from '../../utils/azure';
import AlertDialog from '../AlertDialog';
import { BlockBlobClient } from '@azure/storage-blob';
import { callAPISync, logUploadCompleted, logUploadStarted } from '../../utils/api';
import { v4 as uuidv4 } from 'uuid';
import { getUserOrgsString } from '../../store/user/userSlice';
import UploadStatusDisplay from '../UploadStatusDisplay';
import DragDropUpload from '../DragDropUpload';
import { useLogto } from '@logto/react';
import FileTypeSelector from '../FileTypeSelector';
import { convertFileFormatToText } from '../../utils/string';

const SIZE_LIMIT = import.meta.env.VITE_MAX_UPLOAD_MB || 500;

interface BlobCheck {
	containerName: string;
	fileName: string;
}

interface UploadProps {
	allowedFormats: string[];
	containerName: string;
	forceDisabled?: boolean;
	disabledReason?: string;
}

const FileUpload: React.FC<UploadProps> = ({ allowedFormats, containerName, forceDisabled, disabledReason }) => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const uploadState = useAppSelector((state) => state.upload);
	const userName = useAppSelector((state) => state.user.name);
	const userEmail = useAppSelector((state) => state.user.email);
	const userTeams = useAppSelector((state) => getUserOrgsString(state));
	const fileId = useAppSelector((state) => state.upload.uploadId);
	const uploadReason = useAppSelector((state) => state.upload.uploadReason);
	const suspectKnown = useAppSelector((state) => isSuspectKnown(state));
	const uploadComplete = useAppSelector((state) => getUploadComplete(state));

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFormat, setSelectedFormat] = useState('');
	const [fileError, setFileError] = useState<string>('');
	const [blobClient, setBlobClient] = useState<BlockBlobClient | null>(null);
	const [curBlob, setCurBlob] = useState<BlobCheck>({
		containerName: '',
		fileName: '',
	});
	const [blockUploading, setBlockUploading] = useState<boolean>(false);
	const [foundDuplicate, setFoundDuplicate] = useState<boolean>(false);

	const getProgress = () => {
		const uploadBonus = uploadState.status === UploadStatus.COMPLETE ? 1 : 0;
		return ((uploadState.uploadedBlocks.length + uploadBonus) / (uploadState.blockCount + 1)) * 100;
	};

	const createClient = (fileName: string): void => {
		callAPISync(logto, (token) => {
			generateBlockBlobClient(containerName, fileName, token).then((client) => {
				setBlobClient(client);
				setCurBlob({
					containerName,
					fileName: fileName,
				});
			});
		});
	};

	useEffect(() => {
		dispatch(cancelFileUpload());
	}, []);

	const validateAndCreateClient = async (file: File, container: string) => {
		try {
			if (curBlob.containerName !== container || curBlob.fileName !== file.name) {
				callAPISync(logto, (token) => {
					checkFileExists(container, file.name, token)
						.then((exists) => {
							setFileError('');
							if (exists) {
								setFoundDuplicate(true);
								setBlobClient(null);
							} else {
								setFoundDuplicate(false);
								createClient(file.name);
							}
						})
						.catch((e) => {
							setFileError(String(e));
						});
				});
			}
			setFileError('');
		} catch (e: unknown) {
			setFileError(e instanceof Error ? e.message : String(e));
			setBlobClient(null);
		}
	};

	const handleFileChange = (file: File) => {
		setSelectedFile(file);
		handleFileFormatChange(file.type);

		if (file && containerName) {
			validateAndCreateClient(file, containerName);
		} else {
			setBlobClient(null);
		}
	};

	const handleFileFormatChange = (format: string) => {
		setSelectedFormat(convertFileFormatToText(format));
		dispatch(setFileFormat(convertFileFormatToText(format)));
	};

	async function uploadBlock(file: File, blockClient: BlockBlobClient, curBlock: number, blockSize: number) {
		const start = curBlock * blockSize;
		const end = Math.min(file.size, start + blockSize);
		const blockId = btoa(`${curBlock}`.padStart(20, '0'));
		const chunk = file.slice(start, end);

		await blockClient.stageBlock(blockId, chunk, chunk.size);
		dispatch(addBlock(blockId));
		Logger.debug('Block uploaded');
	}

	// the upload logic sits in useEffect so that it's re-triggered every time a block finishes uploading until the file is done
	useEffect(() => {
		if (uploadState.status === UploadStatus.UPLOADING && blobClient) {
			const curBlock = uploadState.uploadedBlocks.length;

			// only trigger a block upload if there isn't one underway and there's still blocks to go
			if (selectedFile && !blockUploading && curBlock < uploadState.blockCount) {
				Logger.info(`Uploading block ${curBlock + 1} of ${uploadState.blockCount} for ${selectedFile.name}`);

				// eslint-disable-next-line react-hooks/set-state-in-effect
				setBlockUploading(true);
				uploadBlock(selectedFile, blobClient, curBlock, uploadState.blockSize)
					.then(() => {
						setBlockUploading(false);
					})
					.catch((e) => {
						const err = e as Error;
						dispatch(
							updateStatus({
								newStatus: UploadStatus.ERROR,
								desc: err.message,
							})
						);
						setBlockUploading(false);
					});
			}

			// if all of the blocks have been uploaded then finish and commit the upload
			if (curBlock >= uploadState.blockCount) {
				Logger.debug(`Full block list: ${uploadState.uploadedBlocks}`);
				Logger.debug(`Committing blocklist for ${uploadState.currentFileName}`);

				blobClient
					.commitBlockList(uploadState.uploadedBlocks)
					.then(() => {
						setFileError('');
						dispatch(
							updateStatus({
								newStatus: UploadStatus.COMPLETE,
								desc: '',
							})
						);
						callAPISync(logto, (token) => {
							logUploadCompleted(token, uploadState.fileOperation, uploadState.currentFileName, fileId);
						});
						Logger.info('Upload complete!');
					})
					.catch((e) => {
						const err = e as Error;
						dispatch(
							updateStatus({
								newStatus: UploadStatus.ERROR,
								desc: err.message,
							})
						);
					});
			}
		}
	}, [uploadState, blockUploading]);

	const handleSubmit = async () => {
		if (selectedFile) {
			const fileId = uuidv4();
			setFileError('');

			callAPISync(logto, (token) => {
				logUploadStarted(
					token,
					containerName,
					selectedFile.name,
					selectedFile.size,
					userName,
					userTeams,
					userEmail,
					fileId,
					uploadReason,
					suspectKnown
				);
			});

			const numBlocks = Math.ceil(selectedFile.size / uploadState.blockSize);
			dispatch(
				initFileUpload({
					fileName: selectedFile.name,
					fileFormat: selectedFile.type,
					fileContainer: containerName,
					blockCount: numBlocks,
					uploadId: fileId,
					uploadType: 'File Upload',
					warningMessage: `You currently have a file upload in progress: ${selectedFile.name}`,
				})
			);
			Logger.info('File uploading:', selectedFile.name, `(${minifyBytes(selectedFile.size)}, ${numBlocks} block(s))`);
		}
	};

	const cancelUpload = () => {
		setSelectedFile(null);
		setFileError('');
		setBlobClient(null);
		dispatch(cancelFileUpload());
		Logger.info('File upload cancelled');
	};

	return (
		<>
			<Stack spacing={2}>
				<DragDropUpload
					onFileSelect={(newFile) => {
						handleFileChange(newFile);
					}}
					fileError={fileError}
					setFileError={setFileError}
					allowedFormats={allowedFormats}
					maxSizeMB={SIZE_LIMIT}
					disabled={forceDisabled}
					disabledReason={disabledReason}
				/>

				<UploadStatusDisplay
					chosenFile={selectedFile}
					uploadProgressPercent={getProgress()}
					uploadStarted={Boolean(uploadState.status)}
					onUploadStart={handleSubmit}
					submitDisabled={!blobClient}
					isPaused={uploadState.status === UploadStatus.PAUSED}
					onPauseToggle={(isPaused) => {
						dispatch(updateStatus({ newStatus: isPaused ? UploadStatus.PAUSED : UploadStatus.UPLOADING }));
					}}
					onCancel={cancelUpload}
				/>

				<FileTypeSelector
					disabled={!uploadComplete}
					selected={selectedFormat}
					setSelected={handleFileFormatChange}
					options={Array.from(new Set(allowedFormats.map(convertFileFormatToText)))}
					detectedFormat={convertFileFormatToText(selectedFile?.type || '')}
				/>
			</Stack>

			<AlertDialog
				title="Error Uploading File"
				message={[`An error occurred uploading ${selectedFile?.name}:`, `${uploadState.statusDesc}`, `How do you want to proceed?`]}
				isOpen={uploadState.status === UploadStatus.ERROR}
				//tbc
				// leftButtonMessage='Retry'
				// leftButtonAction={() => dispatch(updateStatus({ newStatus: UploadStatus.UPLOADING }))}
				rightButtonMessage="Cancel"
				rightButtonAction={() => cancelUpload()}
			/>

			<AlertDialog
				title="File already exists"
				message={['File with name:', `${selectedFile?.name}`, 'already exists in operation:', containerName, 'Use existing file?']}
				isOpen={foundDuplicate}
				leftButtonMessage="Reupload"
				leftButtonAction={() => {
					setFoundDuplicate(false);
					createClient(`${selectedFile?.name}`);
				}}
				rightButtonMessage={'Use Existing'}
				rightButtonAction={() => {
					const fileId = uuidv4();
					setCurBlob({
						fileName: `${selectedFile?.name}`,
						containerName: containerName,
					});
					dispatch(
						skipFileUpload({
							fileName: `${selectedFile?.name}`,
							fileFormat: `${selectedFile?.type}`,
							fileContainer: containerName,
							processingId: fileId,
							warningMessage: `You currently have a file upload in progress: ${selectedFile?.name}`,
						})
					);
					setFoundDuplicate(false);
				}}
			/>
		</>
	);
};

export default FileUpload;
